import { isNumeric } from '../lensChart';

export type TableSortDirection = 'asc' | 'desc';

export type TableSortState = {
  column: string;
  direction: TableSortDirection;
};

const isMissing = (value: unknown) =>
  value === null || value === undefined || value === '' || (typeof value === 'number' && !Number.isFinite(value));

const labelCollator = new Intl.Collator('en-AU', {
  numeric: true,
  sensitivity: 'base',
});

const sortableLabel = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'bigint' || typeof value === 'boolean') {
    return value.toString();
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return JSON.stringify(value) ?? '';
};

/**
 * Sorts a table by one visible column without mutating the query result.
 * Missing values stay at the bottom in both directions, and equal values keep
 * their incoming order so switching sorts never makes tied rows jump around.
 */
export function sortTableRows(
  rows: Array<Record<string, unknown>>,
  sort: TableSortState,
): Array<Record<string, unknown>> {
  return rows
    .map((row, index) => ({ index, row }))
    .sort((leftEntry, rightEntry) => {
      const left = leftEntry.row[sort.column];
      const right = rightEntry.row[sort.column];
      const leftMissing = isMissing(left);
      const rightMissing = isMissing(right);

      if (leftMissing || rightMissing) {
        if (leftMissing !== rightMissing) {
          return leftMissing ? 1 : -1;
        }
        return leftEntry.index - rightEntry.index;
      }

      const comparison =
        isNumeric(left) && isNumeric(right)
          ? Number(left) - Number(right)
          : labelCollator.compare(sortableLabel(left), sortableLabel(right));
      const directed = sort.direction === 'asc' ? comparison : -comparison;
      return directed || leftEntry.index - rightEntry.index;
    })
    .map(({ row }) => row);
}

/** A newly chosen column starts ascending; repeated clicks toggle direction. */
export function nextTableSort(current: TableSortState | null, column: string): TableSortState {
  return {
    column,
    direction: current?.column === column && current.direction === 'asc' ? 'desc' : 'asc',
  };
}
